import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, ButtonBase, IconButton, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { DirectMaterialVendorBill, ProjectEstimateScopeItem, SavedMaterialAllocation, UnitTypeMaster, MaterialProcurementOrder, MaterialRateItem } from '../pages/OfficeEstimationPage';
import {
  destinationOptions, groupSourcesByMaterial, pendingStockUsageForMaterial, planAllocationSave, searchDestinations, sourcesFromDirectBills, sourcesFromOrders, unallocatedSources, validateAllocationDraft,
  type AllocationDestination, type AllocationDraftLine, type AllocationSourceWithBalance, type PendingStockUsage,
} from '../office/materialAllocation';
import { materialKey, type StockIssue } from '../office/materialStock';
import { BORDER, CountPill, DARK, FAINT, GOLD, GOLD_D, GREEN, MUTED, OutlineButton, PANEL, Pill, PrimaryButton, RED, ScreenBar, ScreenFooter, WARN } from './phoneUi';

const fmt = (value: number) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const fmtMoney = (value: number) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (value?: string) => { if (!value) return ''; const d = new Date(`${value}T00:00:00`); return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
const today = () => new Date().toISOString().slice(0, 10);
const numOf = (value: string) => { const n = Number(value); return Number.isFinite(n) ? n : 0; };
const round3 = (value: number) => Math.round((value + Number.EPSILON) * 1000) / 1000;
/** A clean quantity string from a percentage of the total, without floating-point tails like "33.329999". */
const qtyFromPercent = (percent: number, total: number) => { const value = round3((percent / 100) * total); return value ? String(value) : ''; };
/** What percentage of the total a quantity is, to one decimal, blank when there's nothing typed yet. */
const percentOf = (qty: string, total: number) => { const n = numOf(qty); if (!n || total <= 0) return ''; return String(Math.round((n / total) * 1000) / 10); };
const allocationDestinations = (allocation: SavedMaterialAllocation): AllocationDestination[] => {
  const destinations = Array.isArray(allocation.destinations) ? allocation.destinations.filter(Boolean) : [];
  return destinations.length ? destinations : [allocation.destination].filter(Boolean);
};
const destinationLabel = (destination: AllocationDestination) =>
  `${destination.label}${destination.unitTypeLabel ? ` · ${destination.unitTypeLabel}` : ''}${destination.buildingName ? ` · ${destination.buildingName}` : ''}`;
const allocationDestinationSummary = (allocation: SavedMaterialAllocation) => {
  const destinations = allocationDestinations(allocation);
  if (destinations.length <= 1) return destinations[0] ? destinationLabel(destinations[0]) : '';
  return destinations.map(destinationLabel).join(' + ');
};
const normalizedWords = (value?: string) =>
  String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(/\s+/).filter((word) => word.length > 2 && !['and', 'the', 'materials', 'material'].includes(word));
const labelsLookRelated = (a?: string, b?: string) => {
  const aWords = normalizedWords(a);
  const bWords = normalizedWords(b);
  if (!aWords.length || !bWords.length) return false;
  return aWords.some((word) => bWords.includes(word));
};
const sourceMatchesUsageMaterial = (source: AllocationSourceWithBalance, issue: PendingStockUsage) => {
  if (source.materialId !== issue.materialId) return false;
  const sourceType = String(source.materialAssemblyType || '').trim();
  const issueType = String(issue.materialAssemblyType || '').trim();
  if (sourceType && issueType) return sourceType === issueType;
  return labelsLookRelated(source.materialLabel, issue.materialLabel);
};
const sourcesForStockUsage = (sources: AllocationSourceWithBalance[], issue: PendingStockUsage) => {
  const materialMatches = sources.filter((source) => sourceMatchesUsageMaterial(source, issue));
  const unitMatches = materialMatches.filter((source) => source.unit === issue.unit);
  return unitMatches.length ? unitMatches : materialMatches;
};
const normText = (value?: string) => String(value || '').trim().toLowerCase();
const destinationSuggestionScore = (source: AllocationSourceWithBalance, destination: AllocationDestination) => {
  if (destination.kind === 'general') return 0;
  if (source.materialId && destination.sourceId === source.materialId) {
    const sourceOption = normText(source.quoteOptionId);
    const destinationOption = normText(destination.quoteOptionId);
    if (sourceOption && destinationOption && sourceOption === destinationOption) return 100;
    if (source.materialAssemblyType && normText(source.materialAssemblyType) === normText(destination.assemblyType)) return 90;
    if (sourceOption || destinationOption) return 70;
    return 80;
  }
  const combinedDestination = [destination.group, destination.subgroup, destination.label, destination.assemblyType, destination.variety].filter(Boolean).join(' ');
  return labelsLookRelated(source.materialLabel, combinedDestination) ? 40 : 0;
};
const sortDestinationsForSource = (source: AllocationSourceWithBalance, options: AllocationDestination[]) =>
  [...options].sort((a, b) =>
    destinationSuggestionScore(source, b) - destinationSuggestionScore(source, a)
    || a.group.localeCompare(b.group)
    || a.label.localeCompare(b.label)
  );

/**
 * Assigns material already received or billed to where it was actually used — a Work Item / activity
 * from the estimate, and a building or unit type — so actual cost can, in time, be compared against the
 * estimate. This never blocks or is required for ordering, receiving or billing.
 */
export default function MaterialAllocationPanel({ orders, bills, allocations, unitTypes, scopeItems, materialItems, onSave, onDeleteAllocation, stockIssues, onViewStockUsage }: {
  orders: Pick<MaterialProcurementOrder, 'id' | 'materialId' | 'quoteOptionId' | 'materialAssemblyType' | 'materialLabel' | 'unit' | 'vendor' | 'rateSnapshot' | 'receipts' | 'unplannedRejected'>[];
  bills: DirectMaterialVendorBill[];
  allocations: SavedMaterialAllocation[];
  unitTypes: Pick<UnitTypeMaster, 'id' | 'label' | 'components'>[];
  scopeItems: Pick<ProjectEstimateScopeItem, 'id' | 'label' | 'variety' | 'assemblyType' | 'group' | 'subgroup' | 'scopeType' | 'buildingName' | 'sourceType' | 'sourceId' | 'quoteOptionId'>[];
  materialItems: Pick<MaterialRateItem, 'id' | 'group'>[];
  onSave: (lines: Omit<SavedMaterialAllocation, 'id'>[]) => Promise<void>;
  onDeleteAllocation: (allocation: SavedMaterialAllocation) => void;
  /** Stock issues, shown as reference only next to a source being allocated — never auto-linked. */
  stockIssues: StockIssue[];
  stockUsageSeen: Record<string, string>;
  onViewStockUsage: (materialKey: string) => void;
}) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<AllocationSourceWithBalance | null>(null);
  const [activeUsage, setActiveUsage] = useState<PendingStockUsage | null>(null);
  const [showAllocated, setShowAllocated] = useState(false);

  const options = useMemo(() => destinationOptions(unitTypes as UnitTypeMaster[], scopeItems as ProjectEstimateScopeItem[], materialItems), [unitTypes, scopeItems, materialItems]);
  const unallocated = useMemo(
    () => unallocatedSources([...sourcesFromOrders(orders), ...sourcesFromDirectBills(bills)], allocations),
    [orders, bills, allocations],
  );
  const shown = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return unallocated;
    return unallocated.filter((source) => `${source.materialLabel} ${source.vendor} ${source.reference}`.toLowerCase().includes(text));
  }, [unallocated, query]);
  const groups = useMemo(() => groupSourcesByMaterial(shown, materialItems), [shown, materialItems]);

  const usageScreen = activeUsage ? <StockUsageAllocationScreen
    issue={activeUsage}
    sources={sourcesForStockUsage(unallocated, activeUsage)}
    options={options}
    onViewStockUsage={onViewStockUsage}
    onBack={() => setActiveUsage(null)}
    onSave={async (lines) => { await onSave(lines); setActiveUsage(null); setActive(null); }}
  /> : null;

  const allocateScreen = active && !activeUsage ? <AllocateScreen
    source={active}
    options={options}
    allocations={allocations}
    stockIssues={stockIssues}
    onViewStockUsage={onViewStockUsage}
    onOpenUsage={(issue) => setActiveUsage(issue)}
    onBack={() => setActive(null)}
    onSave={async (lines) => { await onSave(planAllocationSave(active, lines, today())); setActive(null); }}
  /> : null;

  const listContent = <Stack gap={1.5}>
    <TextField size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search material, vendor or reference" inputProps={{ 'aria-label': 'Search unallocated material' }} fullWidth />

    {groups.map(({ group, sources }) => <Box key={group} sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.75, py: 1.1, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ flex: 1, fontWeight: 700, color: DARK, fontSize: 14 }}>{group}</Typography>
        <CountPill n={sources.length} />
      </Stack>
      {sources.map((source) => {
        const pendingUsage = source.materialId ? pendingStockUsageForMaterial(stockIssues, allocations, source.materialId, source.materialAssemblyType).length : 0;
        return <ButtonBase key={source.key} onClick={() => setActive(source)} aria-label={`Allocate ${source.materialLabel} · ${source.reference || source.date}`} sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, textAlign: 'left', justifyContent: 'flex-start', flexWrap: 'wrap',
          px: 1.75, py: 1.25, borderTop: `1px solid ${BORDER}`, '&:active': { bgcolor: PANEL },
          ...(isDesktop && active?.key === source.key ? { bgcolor: PANEL } : {}),
        }}>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
              <Typography sx={{ fontWeight: 500, color: DARK, fontSize: 14, overflowWrap: 'anywhere' }}>{source.materialLabel}</Typography>
              {pendingUsage > 0 && <Pill tone="info">{pendingUsage} stock usage</Pill>}
            </Stack>
            <Typography sx={{ color: MUTED, fontSize: 12, overflowWrap: 'anywhere' }}>{source.vendor} · {fmtDate(source.date)}{source.reference ? ` · ${source.reference}` : ''}{source.sourceType === 'bill' ? ' · No delivery bill' : ''}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 600, color: WARN, fontSize: 14 }}>{fmt(source.remaining)} {source.unit}</Typography>
            <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{source.allocated > 0 ? `${fmt(source.allocated)} already allocated` : 'not yet allocated'}</Typography>
          </Box>
        </ButtonBase>;
      })}
    </Box>)}
    {!groups.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', bgcolor: '#fff' }}>
      <Typography sx={{ p: 2.5, color: MUTED, fontSize: 13 }}>{unallocated.length ? `Nothing matches "${query.trim()}".` : 'Every received or billed material has been allocated.'}</Typography>
    </Box>}

    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
      <ButtonBase onClick={() => setShowAllocated((value) => !value)} sx={{
        width: '100%', display: 'flex', alignItems: 'center', px: 1.75, py: 1.1, bgcolor: PANEL, borderBottom: showAllocated ? `1px solid ${BORDER}` : 0,
      }}>
        <Typography sx={{ flex: 1, fontWeight: 700, color: DARK, fontSize: 14, textAlign: 'left' }}>Allocated so far ({allocations.length})</Typography>
        <Typography sx={{ color: FAINT, fontSize: 13 }}>{showAllocated ? '▾' : '▸'}</Typography>
      </ButtonBase>
      {showAllocated && <Stack>
        {[...allocations].sort((a, b) => b.allocatedDate.localeCompare(a.allocatedDate)).map((allocation) => <Stack key={allocation.id} direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ px: 1.75, py: 1, borderTop: `1px solid ${BORDER}` }}>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 13.5, overflowWrap: 'anywhere' }}>{allocation.materialLabel}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12, overflowWrap: 'anywhere' }}>{allocationDestinationSummary(allocation)}</Typography>
            {allocationDestinations(allocation).length > 1 && <Typography sx={{ color: GOLD_D, fontSize: 11.5, fontWeight: 700 }}>Shared with {allocationDestinations(allocation).length} work items · counted once</Typography>}
            {allocation.note && <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{allocation.note}</Typography>}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{fmt(allocation.qty)} {allocation.unit}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{allocation.rate > 0 ? fmtMoney(allocation.amount) : 'no rate yet'} · {fmtDate(allocation.allocatedDate)}</Typography>
          </Box>
          <IconButton size="small" aria-label={`Remove allocation of ${allocation.materialLabel}`} onClick={() => onDeleteAllocation(allocation)}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Stack>)}
        {!allocations.length && <Typography sx={{ p: 2.5, color: MUTED, fontSize: 13 }}>Nothing has been allocated yet.</Typography>}
      </Stack>}
    </Box>
  </Stack>;

  if (!isDesktop) return usageScreen || (active ? allocateScreen : listContent);

  return <Stack direction="row" gap={2} alignItems="flex-start">
    <Box sx={{ flex: '1 1 420px', maxWidth: 520 }}>{listContent}</Box>
    <Box sx={{
      flex: '1 1 420px', position: 'sticky', top: 16, alignSelf: 'flex-start', minHeight: 420,
      maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', borderRadius: '15px',
      bgcolor: usageScreen || allocateScreen ? '#fff' : PANEL, border: usageScreen || allocateScreen ? `1px solid ${BORDER}` : 'none',
    }}>
      {usageScreen || allocateScreen || <Stack sx={{ height: '100%', minHeight: 420, alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 4 }}>
        <Typography sx={{ color: MUTED, fontSize: 15 }}>Pick a material's waiting delivery to allocate it, or open one to see its split.</Typography>
      </Stack>}
    </Box>
  </Stack>;
}

function AllocateScreen({ source, options, allocations, stockIssues, onViewStockUsage, onOpenUsage, onBack, onSave }: {
  source: AllocationSourceWithBalance;
  options: AllocationDestination[];
  allocations: SavedMaterialAllocation[];
  stockIssues: StockIssue[];
  onViewStockUsage: (materialKey: string) => void;
  onOpenUsage: (issue: PendingStockUsage) => void;
  onBack: () => void;
  onSave: (lines: AllocationDraftLine[]) => Promise<void>;
}) {
  const [lines, setLines] = useState<AllocationDraftLine[]>([{ destination: null, destinations: [], qty: String(source.remaining), note: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const problems = validateAllocationDraft(source, lines);
  const setLine = (index: number, patch: Partial<AllocationDraftLine>) => setLines((previous) => previous.map((line, at) => at === index ? { ...line, ...patch } : line));
  const rankedOptions = useMemo(() => sortDestinationsForSource(source, options), [source, options]);
  const strongSuggestions = useMemo(() => rankedOptions.filter((option) => destinationSuggestionScore(source, option) >= 70), [rankedOptions, source]);

  const usage = source.materialId ? pendingStockUsageForMaterial(stockIssues, allocations, source.materialId, source.materialAssemblyType) : [];
  useEffect(() => {
    if (strongSuggestions.length !== 1) return;
    setLines((previous) => {
      const [first, ...rest] = previous;
      if (!first || first.destination || (first.destinations || []).length) return previous;
      return [{ ...first, destination: strongSuggestions[0], destinations: [strongSuggestions[0]] }, ...rest];
    });
  }, [source.key, strongSuggestions]);
  useEffect(() => {
    if (source.materialId) onViewStockUsage(materialKey(source.materialId, source.materialAssemblyType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.materialId, source.materialAssemblyType]);

  const allocatedSoFar = round3(lines.reduce((sum, line) => sum + numOf(line.qty), 0));
  const pending = round3(source.remaining - allocatedSoFar);
  const allocatedPercent = source.remaining > 0 ? Math.round((allocatedSoFar / source.remaining) * 100) : 0;

  const save = async () => {
    setSaving(true); setError('');
    try { await onSave(lines); } catch (err: any) { setError(err?.message || 'Could not save this allocation.'); setSaving(false); }
  };

  return <Stack sx={{ minHeight: 0 }}>
    <ScreenBar title={`Allocate ${source.materialLabel}`} sub={`${source.vendor} · ${fmtDate(source.date)} · ${fmt(source.remaining)} ${source.unit} left to allocate`} onBack={saving ? undefined : onBack} />
    <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 1.75, py: 1.1, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: DARK }}>{fmt(allocatedSoFar)} {source.unit} allocated <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>({allocatedPercent}%)</Box></Typography>
      <Typography sx={{ flex: 1, textAlign: 'right', fontWeight: 600, fontSize: 13.5, color: pending < -0.001 ? RED : pending > 0.001 ? WARN : GREEN }}>
        {pending < -0.001 ? `${fmt(Math.abs(pending))} ${source.unit} over` : `${fmt(pending)} ${source.unit} pending`}
      </Typography>
    </Stack>
    <Stack gap={1.5} sx={{ px: 1.75, py: 1.5 }}>
      {lines.map((line, index) => <Box key={index} sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: '15px' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={rankedOptions}
          groupBy={(option) => destinationSuggestionScore(source, option) >= 40 ? 'Suggested for this material' : option.group}
          getOptionLabel={(option) => option.unitTypeLabel ? `${option.label} · ${option.unitTypeLabel}` : option.label}
          filterOptions={(list, state) => sortDestinationsForSource(source, searchDestinations(list, state.inputValue))}
          value={line.destinations || (line.destination ? [line.destination] : [])}
          onChange={(_, value) => setLine(index, { destinations: value, destination: value[0] || null })}
          isOptionEqualToValue={(option, value) => option.kind === value.kind && option.id === value.id}
          renderOption={(props, option) => {
            const score = destinationSuggestionScore(source, option);
            return <Box component="li" {...props} sx={{ display: 'block !important', py: '8px !important' }}>
              <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{option.unitTypeLabel ? `${option.label} · ${option.unitTypeLabel}` : option.label}</Typography>
                {score >= 70 && <Pill tone="info">same material</Pill>}
                {score > 0 && score < 70 && <Pill tone="warn">possible match</Pill>}
              </Stack>
              <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{[option.group, option.subgroup, option.buildingName].filter(Boolean).join(' · ') || 'General'}</Typography>
            </Box>;
          }}
          renderInput={(params) => <TextField {...params} label="Work Item / activity" placeholder="Select one or more work items" helperText={(line.destinations || []).length > 1 ? 'Shared allocation: this quantity is counted once, linked to all selected work items.' : 'Select multiple only when exact split is not known.'} inputProps={{ ...params.inputProps, 'aria-label': `Destination ${index + 1}` }} />}
        />
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
          <TextField label={`Quantity (${source.unit})`} type="number" value={line.qty} onChange={(event) => setLine(index, { qty: event.target.value })} inputProps={{ min: 0, step: 'any', 'aria-label': `Quantity ${index + 1}` }} sx={{ flex: '2 1 140px' }} />
          <TextField label="% of total" type="number" value={percentOf(line.qty, source.remaining)} onChange={(event) => setLine(index, { qty: qtyFromPercent(Number(event.target.value) || 0, source.remaining) })} inputProps={{ min: 0, max: 100, step: 'any', 'aria-label': `Percent ${index + 1}` }} sx={{ flex: '1 1 100px' }} />
          <TextField label="Note (optional)" value={line.note} onChange={(event) => setLine(index, { note: event.target.value })} placeholder="e.g. Floor 3, Wing B" inputProps={{ 'aria-label': `Note ${index + 1}` }} sx={{ flex: '2 1 160px' }} />
        </Stack>
        {lines.length > 1 && <ButtonBase onClick={() => setLines((previous) => previous.filter((_, at) => at !== index))} sx={{ mt: 0.75, color: WARN, fontSize: 13, fontWeight: 600 }}>Remove this destination</ButtonBase>}
      </Box>)}
      <ButtonBase onClick={() => setLines((previous) => [...previous, { destination: null, destinations: [], qty: '', note: '' }])} sx={{ alignSelf: 'flex-start', color: GOLD_D, fontWeight: 600, fontSize: 14 }}>+ Split by quantity into another destination</ButtonBase>
      {error && <Typography sx={{ color: 'error.main', fontSize: 13 }}>{error}</Typography>}

      {!!usage.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: DARK }}>Pending stock usage for this material</Typography>
          <Typography sx={{ color: MUTED, fontSize: 11.5 }}>Click a usage to allocate its full quantity across challans.</Typography>
        </Box>
        {usage.map((issue) => {
          return <ButtonBase key={issue.id} onClick={() => onOpenUsage(issue)} sx={{ width: '100%', display: 'flex', alignItems: 'center', textAlign: 'left', gap: 1.25, flexWrap: 'wrap', px: 1.5, py: 1, borderTop: `1px solid ${BORDER}`, '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: PANEL } }}>
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 13 }}>{issue.destination.label}</Typography>
              <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{fmtDate(issue.date)}{issue.note ? ` · ${issue.note}` : ''}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fmt(issue.remaining)} {issue.unit}</Typography>
              {issue.allocated > 0 && <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{fmt(issue.allocated)} allocated</Typography>}
            </Box>
          </ButtonBase>;
        })}
      </Box>}
    </Stack>
    <ScreenFooter>
      {problems.length > 0 && <Typography sx={{ flex: 1, color: WARN, fontSize: 12 }}>To save: {problems.join('; ')}</Typography>}
      {!problems.length && <Box sx={{ flex: 1 }} />}
      <OutlineButton onClick={onBack} disabled={saving}>Cancel</OutlineButton>
      <PrimaryButton disabled={!!problems.length || saving} onClick={save} sx={{ bgcolor: GOLD }}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
    </ScreenFooter>
  </Stack>;
}

function StockUsageAllocationScreen({ issue, sources, options, onViewStockUsage, onBack, onSave }: {
  issue: PendingStockUsage;
  sources: AllocationSourceWithBalance[];
  options: AllocationDestination[];
  onViewStockUsage: (materialKey: string) => void;
  onBack: () => void;
  onSave: (lines: Omit<SavedMaterialAllocation, 'id'>[]) => Promise<void>;
}) {
  const [destination, setDestination] = useState<AllocationDestination | null>(null);
  const [sourceQtys, setSourceQtys] = useState<Record<string, string>>({});
  const [note, setNote] = useState([issue.destination.label, issue.note].filter(Boolean).join(' · '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const suggestedQtys = useMemo(() => {
    let remaining = issue.remaining;
    const next: Record<string, string> = {};
    sources.forEach((source) => {
      if (remaining <= 0) return;
      const qty = round3(Math.min(remaining, source.remaining));
      if (qty > 0) next[source.key] = String(qty);
      remaining = round3(remaining - qty);
    });
    return next;
  }, [issue.id, issue.remaining, sources]);

  useEffect(() => { setSourceQtys(suggestedQtys); }, [suggestedQtys]);
  useEffect(() => {
    onViewStockUsage(materialKey(issue.materialId, issue.materialAssemblyType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.materialId, issue.materialAssemblyType]);

  const selectedTotal = round3(sources.reduce((sum, source) => sum + numOf(sourceQtys[source.key] || ''), 0));
  const diff = round3(issue.remaining - selectedTotal);
  const overSource = sources.find((source) => numOf(sourceQtys[source.key] || '') - source.remaining > 0.005);
  const problems = [
    !destination ? 'choose the Work Item / activity' : '',
    overSource ? `${overSource.reference || overSource.date} has only ${fmt(overSource.remaining)} ${sourceUnitLabel(overSource)}` : '',
    !overSource && Math.abs(diff) > 0.005 ? (diff > 0 ? `select ${fmt(diff)} ${issue.unit} more from challans` : `remove ${fmt(Math.abs(diff))} ${issue.unit} from challans`) : '',
  ].filter(Boolean);

  const save = async () => {
    if (!destination || problems.length) return;
    setSaving(true); setError('');
    try {
      const lines = sources
        .map((source) => ({ source, qty: numOf(sourceQtys[source.key] || '') }))
        .filter(({ qty }) => qty > 0)
        .map(({ source, qty }) => ({
          sourceKey: source.key,
          sourceType: source.sourceType,
          orderId: source.orderId, receiptId: source.receiptId, billId: source.billId, billLineKey: source.billLineKey,
          materialId: source.materialId, quoteOptionId: source.quoteOptionId, materialLabel: source.materialLabel, unit: source.unit,
          qty, rate: source.rate, amount: Math.round((qty * source.rate + Number.EPSILON) * 100) / 100,
          destination, stockIssueId: issue.id, note: note.trim(), allocatedDate: today(),
        }));
      await onSave(lines);
    } catch (err: any) {
      setError(err?.message || 'Could not save this allocation.');
      setSaving(false);
    }
  };

  return <Stack sx={{ minHeight: 0 }}>
    <ScreenBar title={`Allocate stock usage · ${issue.materialLabel}`} sub={`${fmt(issue.remaining)} ${issue.unit} used by ${issue.destination.label}`} onBack={saving ? undefined : onBack} />
    <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 1.75, py: 1.1, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: DARK }}>{fmt(selectedTotal)} {issue.unit} selected</Typography>
      <Typography sx={{ flex: 1, textAlign: 'right', fontWeight: 600, fontSize: 13.5, color: Math.abs(diff) <= 0.005 ? GREEN : WARN }}>
        {Math.abs(diff) <= 0.005 ? 'Matched' : diff > 0 ? `${fmt(diff)} ${issue.unit} pending` : `${fmt(Math.abs(diff))} ${issue.unit} over`}
      </Typography>
    </Stack>
    <Stack gap={1.5} sx={{ px: 1.75, py: 1.5 }}>
      <Autocomplete
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.unitTypeLabel ? `${option.label} · ${option.unitTypeLabel}` : option.label}
        filterOptions={(list, state) => searchDestinations(list, state.inputValue)}
        value={destination}
        onChange={(_, value) => setDestination(value)}
        renderInput={(params) => <TextField {...params} label="Work Item / activity" placeholder="Search the estimate's activities" inputProps={{ ...params.inputProps, 'aria-label': 'Stock usage destination' }} />}
      />
      <TextField label="Note (optional)" value={note} onChange={(event) => setNote(event.target.value)} fullWidth />

      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: DARK }}>Select challans for this usage</Typography>
          <Typography sx={{ color: MUTED, fontSize: 11.5 }}>Selected challan quantity must equal {fmt(issue.remaining)} {issue.unit}.</Typography>
        </Box>
        {sources.map((source) => {
          const value = sourceQtys[source.key] || '';
          return <Stack key={source.key} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.1} sx={{ px: 1.5, py: 1, borderTop: `1px solid ${BORDER}` }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: DARK }}>{source.reference || 'No challan number'}</Typography>
              <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{source.vendor} · {fmtDate(source.date)} · {fmt(source.remaining)} {source.unit} available</Typography>
            </Box>
            <TextField
              label={`Use (${source.unit})`}
              type="number"
              value={value}
              onChange={(event) => { setSourceQtys((previous) => ({ ...previous, [source.key]: event.target.value })); setError(''); }}
              inputProps={{ min: 0, max: source.remaining, step: 'any', 'aria-label': `Use from ${source.reference || source.date}` }}
              sx={{ width: { xs: '100%', sm: 150 } }}
            />
          </Stack>;
        })}
        {!sources.length && <Typography sx={{ p: 2, color: MUTED, fontSize: 13 }}>No unallocated challan quantity is available for this material.</Typography>}
      </Box>

      {error && <Typography sx={{ color: 'error.main', fontSize: 13 }}>{error}</Typography>}
    </Stack>
    <ScreenFooter>
      {problems.length > 0 && <Typography sx={{ flex: 1, color: WARN, fontSize: 12 }}>To save: {problems.join('; ')}</Typography>}
      {!problems.length && <Box sx={{ flex: 1 }} />}
      <OutlineButton onClick={onBack} disabled={saving}>Cancel</OutlineButton>
      <PrimaryButton disabled={!!problems.length || saving || !sources.length} onClick={save}>{saving ? 'Saving…' : 'Save allocation'}</PrimaryButton>
    </ScreenFooter>
  </Stack>;
}

function sourceUnitLabel(source: Pick<AllocationSourceWithBalance, 'unit'>) {
  return source.unit || 'unit';
}
